import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { Button } from '../../../../components/ui/Button';
import { PendingButton } from '../../../../components/ui/PendingButton';
import { CandidatesSheet } from '../../components/CandidatesSheet';
import { OperatorActionBar } from '../../components/OperatorActionBar';
import { OperatorScanPanel } from '../../components/OperatorScanPanel';
import { OperatorTabs } from '../../components/OperatorTabs';
import { QuantityStepper } from '../../components/QuantityStepper';
import { QueueList } from '../../components/QueueList';
import { SlotSuggestions } from '../../components/SlotSuggestions';
import { taskEntered } from '../../../../features/operator/operatorDeviceSlice';
import { OPERATOR_FLOW } from '../../../../features/operator/operatorConstants';
import { SCAN_SOURCE } from '../../../../features/operator/operatorScanGate';
import { putawayRowChosen } from '../../../../features/operatorPutaway/operatorPutawayActions';
import {
  choosePutawayOption,
  fetchPutawayPending,
  fetchPutawayRow,
  scanPutawayCode,
  storePutaway,
} from '../../../../features/operatorPutaway/operatorPutawayThunks';
import {
  putawayEntered,
  putawayLeft,
  putawayQuantityCancelled,
  putawayQuantityRequested,
  putawayResolutionDismissed,
  putawaySelectionCancelled,
  putawaySourceChanged,
  selectPutawayAccepting,
  selectPutawayRow,
} from '../../../../features/operatorPutaway/operatorPutawaySlice';
import {
  PUTAWAY_SOURCE,
  PUTAWAY_STEP,
  putawayQuantityProblem,
  rowSuggestions,
} from '../../../../features/operatorPutaway/operatorPutawayUtils';
import { formatQuantity, PUTAWAY_SOURCE_LABELS } from '../../../../utils/inventoryLabels';

const rowSubtitle = (row) =>
  [
    row.slot_name || row.slot_code ? `en ${row.slot_name || row.slot_code}` : null,
    row.lot ? `lote ${row.lot}` : null,
    row.order_reference ? `orden ${row.order_reference}` : null,
    row.company_name,
  ]
    .filter(Boolean)
    .join(' · ');

/** Where what is put away comes from, each tab with its rows when the server counted them. */
const sourceTabs = (counts) =>
  [PUTAWAY_SOURCE.RECEIVING, PUTAWAY_SOURCE.RESTOCK].map((value) => ({
    value,
    label: PUTAWAY_SOURCE_LABELS[value],
    count: counts?.[value]?.rows,
  }));

/**
 * The row being stored and where the server suggests it goes; while it asks
 * where, tapping a suggestion reads it as its label would.
 */
const SelectedRow = ({ row, destination, interactive = false, disabled = false, onTap }) => {
  const suggestions = rowSuggestions(row);
  return (
    <section className="space-y-3 rounded-3xl border border-border bg-white p-4 dark:bg-[#1a1a1a]">
      <div>
        <p className="text-lg font-bold text-ninja-dark dark:text-white">
          {formatQuantity(row.quantity)} un · {row.product_sku}
        </p>
        <p className="text-sm text-text-secondary">{row.product_name}</p>
        <p className="text-sm text-text-secondary">{rowSubtitle(row)}</p>
      </div>
      <SlotSuggestions
        suggestions={suggestions}
        interactive={interactive}
        disabled={disabled}
        onTap={onTap}
        testId="putaway-suggestion"
      />
      {destination && (
        <p
          data-testid="putaway-destination"
          data-suggested={String(destination.suggested)}
          className={clsx(
            'rounded-2xl px-3 py-2 text-sm font-bold',
            destination.suggested ? 'bg-ninja-green text-white' : 'bg-amber-400 text-ninja-dark'
          )}
        >
          Destino: {destination.name || destination.code}
          {destination.suggested ? ' · sugerida' : ' · no es la sugerida'}
        </p>
      )}
    </section>
  );
};

/**
 * Almacenado: what was received or comes back from orders, to be put away.
 * Scan the product (or tap its row), see where it should go, scan where it
 * goes, and store all of it — or another quantity. A task of the tray opens
 * it on its row (`?source=&key=`).
 */
export const PutawayTask = () => {
  const dispatch = useDispatch();
  const [params] = useSearchParams();
  const putaway = useSelector((state) => state.operatorPutaway);
  const row = useSelector(selectPutawayRow);
  const accepting = useSelector(selectPutawayAccepting);
  const { source, list, step, destination, pending, resolution } = putaway;
  const [quantity, setQuantity] = useState('');
  const requestedKey = params.get('key');
  const requestedSource =
    params.get('source') === PUTAWAY_SOURCE.RESTOCK ? PUTAWAY_SOURCE.RESTOCK : PUTAWAY_SOURCE.RECEIVING;

  useEffect(() => {
    dispatch(putawayEntered());
    dispatch(taskEntered({ flow: OPERATOR_FLOW.PUTAWAY }));
    return () => {
      dispatch(putawayLeft());
    };
  }, [dispatch]);

  useEffect(() => {
    if (requestedKey) dispatch(fetchPutawayRow({ source: requestedSource, key: requestedKey }));
  }, [dispatch, requestedKey, requestedSource]);

  useEffect(() => {
    dispatch(fetchPutawayPending({ page: 1 }));
  }, [dispatch, source]);

  const read = (code, scanSource) => dispatch(scanPutawayCode({ code, source: scanSource }));

  const store = (amount) =>
    dispatch(
      storePutaway({
        rowKey: row.key,
        source: row.source,
        sku: row.product_sku,
        bucketId: row.bucket_id,
        orderProductId: row.order_product,
        lot: row.source === PUTAWAY_SOURCE.RESTOCK ? row.lot : undefined,
        toSlotCode: destination.code,
        toSlotName: destination.name,
        quantity: Number(amount),
        rowQuantity: Number(row.quantity),
      })
    );

  // Another quantity is counted from 0 up to what the row holds.
  const askQuantity = () => {
    setQuantity('0');
    dispatch(putawayQuantityRequested());
  };

  const problem = row ? putawayQuantityProblem(quantity, row) : null;
  const atProduct = step === PUTAWAY_STEP.PRODUCT;
  const atDestination = Boolean(row) && step === PUTAWAY_STEP.DESTINATION;
  const destinationHint = atDestination && rowSuggestions(row).length > 0 ? 'O toca una sugerida arriba.' : undefined;

  return (
    <div className="space-y-5" data-testid="putaway-task" data-step={step}>
      {/* One camera for the product and where it goes. Moving on offers nothing again: what is in view then
          is the product just scanned, not a slot. */}
      {(atProduct || atDestination) && (
        <>
          {atProduct ? (
            <OperatorTabs
              tabs={sourceTabs(list.counts)}
              active={source}
              onChange={(value) => dispatch(putawaySourceChanged(value))}
              testId="putaway-source-tab"
              dataKey="source"
              label="Qué guardar"
            />
          ) : (
            <SelectedRow
              row={row}
              destination={destination}
              interactive
              disabled={!accepting}
              onTap={(code) => read(code, SCAN_SOURCE.TAP)}
            />
          )}
          <OperatorScanPanel
            prompt={atProduct ? 'Escanea el producto a guardar' : 'Escanea dónde lo guardas'}
            hint={atProduct ? 'O toca su fila.' : destinationHint}
            accepting={accepting}
            lastCode={putaway.scan.lastCode}
            busyLabel={pending ? 'Guardando…' : 'Buscando…'}
            onCode={read}
          />
        </>
      )}

      {atProduct && (
        <QueueList
          queue={{ ...list, items: list.rows }}
          testId="putaway-list"
          emptyText="No hay nada pendiente de guardar."
          onRetry={() => dispatch(fetchPutawayPending({ page: 1 }))}
          onMore={() => dispatch(fetchPutawayPending({ page: list.page + 1 }))}
          onRefresh={() => dispatch(fetchPutawayPending({ page: 1 }))}
          refreshThunk={fetchPutawayPending}
        >
          {list.rows.map((item) => (
            <li key={item.key}>
              <Button
                variant="choice"
                data-testid="putaway-row"
                data-row-key={item.key}
                onClick={() => dispatch(putawayRowChosen(item.key))}
              >
                <span className="flex items-start justify-between gap-3">
                  <span className="min-w-0">
                    <span className="block font-bold text-ninja-dark dark:text-white">{item.product_sku}</span>
                    <span className="block truncate text-sm text-text-secondary">{item.product_name}</span>
                    <span className="block text-sm text-text-secondary">{rowSubtitle(item)}</span>
                  </span>
                  <span className="shrink-0 text-lg font-bold text-ninja-dark dark:text-white">
                    {formatQuantity(item.quantity)} un
                  </span>
                </span>
              </Button>
            </li>
          ))}
        </QueueList>
      )}

      {atDestination && (
        <>
          <OperatorActionBar>
            <Button
              variant="ghost"
              size="lg"
              disabled={Boolean(pending) || !destination}
              onClick={askQuantity}
              data-testid="putaway-other-quantity"
            >
              Otra cantidad
            </Button>
            <PendingButton
              pendingOn={storePutaway}
              variant="primary"
              size="lg"
              disabled={!destination}
              loadingLabel="Guardando…"
              onClick={() => store(row.quantity)}
              data-testid="putaway-all-here"
            >
              {destination ? `Guardar todo · ${formatQuantity(row.quantity)} un` : 'Escanea el destino'}
            </PendingButton>
          </OperatorActionBar>
          <Button variant="ghost" size="lg" fullWidth onClick={() => dispatch(putawaySelectionCancelled())}>
            Elegir otro producto
          </Button>
        </>
      )}

      {row && step === PUTAWAY_STEP.QUANTITY && (
        <>
          <SelectedRow row={row} destination={destination} />
          <QuantityStepper
            label="¿Cuántos guardas?"
            value={quantity}
            onChange={setQuantity}
            min={0}
            max={Number(row.quantity)}
            target={Number(row.quantity)}
            allowDecimals={Boolean(row.allows_fractional_qty)}
            unit="un por guardar"
          />
          <OperatorActionBar>
            <Button variant="ghost" size="lg" disabled={Boolean(pending)} onClick={() => dispatch(putawayQuantityCancelled())}>
              Volver
            </Button>
            <PendingButton
              pendingOn={storePutaway}
              variant="primary"
              size="lg"
              disabled={Boolean(problem)}
              loadingLabel="Guardando…"
              onClick={() => store(quantity)}
              data-testid="putaway-submit"
            >
              {problem || `Guardar ${formatQuantity(quantity)} un en ${destination?.name || destination?.code}`}
            </PendingButton>
          </OperatorActionBar>
        </>
      )}

      <CandidatesSheet
        isOpen={resolution?.kind === 'ambiguous'}
        message={resolution?.message}
        options={resolution?.options}
        onChoose={(id) => dispatch(choosePutawayOption(id))}
        onClose={() => dispatch(putawayResolutionDismissed())}
        busy={pending === 'store'}
      />
    </div>
  );
};
